/**
 * Credits store — manages credit balance, history, and costs globally.
 * Flat cost structure — no standard/deep pairs.
 */

import { create } from 'zustand';
import { api } from '../api/client';

export const CREDIT_COSTS: Record<string, number> = {
  // Pipeline steps (charged on 2nd pipeline onwards)
  PIPELINE_PROFILE:     2,
  PIPELINE_IDEA:        3,
  PIPELINE_REALITY:     4,
  PIPELINE_POSITIONING: 3,
  PIPELINE_VALIDATION:  4,
  PIPELINE_MVP:         5,

  // Deep Analysis upgrades (any pipeline)
  DEEP_PROFILE:     2,
  DEEP_IDEA:        3,
  DEEP_REALITY:     4,
  DEEP_POSITIONING: 3,
  DEEP_VALIDATION:  4,
  DEEP_MVP:         5,

  // Intelligence modules
  INTEL_NAME:        1,
  INTEL_KEYWORDS:    2,
  INTEL_COMPETITORS: 3,
  INTEL_TRENDS:      2,
  INTEL_SENTIMENT:   2,
  INTEL_RUN_ALL:     8,

  // Other dashboard actions
  DEEP_SUMMARY:      2,
  DEEP_FINANCIAL:    3,
  DEEP_BETA_PERSONA: 1,
};

export const CREDIT_PACKS = [
  { id: 'STARTER', credits: 100, price: 9.90,  regularPrice: 9.90,  betaPrice: 4.95,  label: 'Starter', tagline: 'Get started validating',       hint: '~6 full pipelines + intel modules',   emoji: '✨' },
  { id: 'GROWTH',  credits: 250, price: 19.90, regularPrice: 19.90, betaPrice: 9.95,  label: 'Growth',  tagline: 'Validate multiple ideas',      hint: '~15 pipelines + full intel suite',     emoji: '🚀', bestValue: true },
  { id: 'PRO',     credits: 500, price: 29.90, regularPrice: 29.90, betaPrice: 14.95, label: 'Pro',     tagline: 'Full power validation',         hint: 'Unlimited validation for months',      emoji: '⚡' },
];

export const SUBSCRIPTION_TIERS = {
  free:    { name: 'Free',    price: 0,   monthlyCredits: 25,    maxRolloverCap: 25,    dailyPipelineLimit: 2 },
  indie:   { name: 'Indie',   price: 19,  monthlyCredits: 300,   maxRolloverCap: 600,   dailyPipelineLimit: 5 },
  builder: { name: 'Builder', price: 49,  monthlyCredits: 1000,  maxRolloverCap: 2000,  dailyPipelineLimit: 15 },
  studio:  { name: 'Studio',  price: 149, monthlyCredits: 4000,  maxRolloverCap: 8000,  dailyPipelineLimit: 50 },
} as const;

export type SubscriptionTierKey = keyof typeof SUBSCRIPTION_TIERS;

export function getCost(action: string): number {
  return CREDIT_COSTS[action] ?? 0;
}

export function getDeepCost(action: string): number {
  const deepKey = 'DEEP_' + action.replace(/^PIPELINE_/, '');
  return CREDIT_COSTS[deepKey] ?? 0;
}

interface CreditHistoryEntry {
  action: string;
  amount: number;
  deep?: boolean;
  pipelineId?: string;
  startupId?: string;
  pack?: string;
  note?: string;
  createdAt: string;
}

interface Subscription {
  plan: string;
  status: string;
  currentPeriodEnd?: string;
  stripeSubscriptionId?: string;
}

interface SubscriptionStatus {
  plan: string;
  planName: string;
  credits: number;
  monthlyCredits: number;
  rolloverCap: number;
  dailyPipelineLimit: number;
  dailyPipelinesUsed: number;
  billingEnd: string | null;
  status: string;
}

interface CreditsState {
  credits: number;
  totalPurchased: number;
  totalSpent: number;
  pipelineReviewGiven: boolean;
  history: CreditHistoryEntry[];
  subscription: Subscription | null;
  subscriptionStatus: SubscriptionStatus | null;
  loading: boolean;
  loaded: boolean;

  fetchBalance: () => Promise<void>;
  optimisticDeduct: (n: number) => void;
  optimisticRestore: (n: number) => void;
  checkout: (pack: string) => Promise<void>;
  subscribe: () => Promise<void>;
  manage: () => Promise<void>;
  subscribeCheckout: (plan: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  fetchSubscriptionStatus: () => Promise<void>;
}

export const useCreditsStore = create<CreditsState>((set, get) => ({
  credits: 0,
  totalPurchased: 0,
  totalSpent: 0,
  pipelineReviewGiven: false,
  history: [],
  subscription: null,
  subscriptionStatus: null,
  loading: false,
  loaded: false,

  fetchBalance: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const { data } = await api.get('/credits/balance');
      set({
        credits: data.credits,
        totalPurchased: data.totalPurchased,
        totalSpent: data.totalSpent,
        pipelineReviewGiven: data.pipelineReviewGiven ?? false,
        history: data.history || [],
        subscription: data.subscription ?? null,
        loading: false,
        loaded: true,
      });
    } catch {
      set({ loading: false, loaded: true });
    }
  },

  optimisticDeduct: (n: number) => {
    set((s) => ({ credits: Math.max(0, s.credits - n) }));
  },

  // FIX: #1 — restore credits on failed AI step, capped at totalPurchased
  optimisticRestore: (n: number) => {
    set((s) => ({ credits: Math.min(s.totalPurchased || Infinity, s.credits + n) }));
  },

  checkout: async (pack: string) => {
    const { data } = await api.post('/credits/checkout', { pack });
    if (data.url) {
      window.location.href = data.url;
    }
  },

  subscribe: async () => {
    const { data } = await api.post('/credits/subscribe');
    if (data.url) {
      window.location.href = data.url;
    }
  },

  manage: async () => {
    const { data } = await api.post('/credits/manage-subscription');
    if (data.url) {
      window.location.href = data.url;
    }
  },

  subscribeCheckout: async (plan: string) => {
    const { data } = await api.post('/subscriptions/create-checkout', { plan });
    if (data.url) {
      window.location.href = data.url;
    }
  },

  cancelSubscription: async () => {
    await api.post('/subscriptions/cancel');
    // Refresh status
    await get().fetchSubscriptionStatus();
    await get().fetchBalance();
  },

  fetchSubscriptionStatus: async () => {
    try {
      const { data } = await api.get('/subscriptions/status');
      set({ subscriptionStatus: data });
    } catch {
      // ignore
    }
  },
}));
